import { getInternalAppsiteUrl } from '@/lib/hec';
import { persistentAtom } from '@nanostores/persistent';
import { jwtDecode } from 'jwt-decode';
import { fetchWithVersion } from './utils';

export const $customerAuth = persistentAtom<Record<string, AuthState>>(
  'customer-auth',
  {},
  {
    encode: JSON.stringify,
    decode: JSON.parse,
  },
);

declare interface AuthState {
  accessToken?: string;
  refreshToken?: string;
}

export async function login(email: string, password: string, shortID: string) {
  const base = await getInternalAppsiteUrl(shortID);
  const url = `${base}/api/login`;
  const res = await fetchWithVersion(url, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'X-Requested-With': 'checkinapp',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      password,
    }),
  });
  if (!res.ok) {
    throw new Error('login_error');
  }
  const json = await res.json();
  $customerAuth.set({ ...$customerAuth.get(),[shortID]: json });
}

export async function registerDemo(
  appsiteUrl: string,
  data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  },
  shortID: string,
) {
  const url = `${appsiteUrl}/demo-account`;
  const res = await fetchWithVersion(url, {
    method: 'POST',
    headers: {
      accept: 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    throw new Error('register_demo_error');
  }
  const json = await res.json();
  $customerAuth.set({ ...$customerAuth.get(),[shortID]: json });
}

export async function getAuth(customer: string) {
  return $customerAuth.get()[customer];
}

export interface DecodedToken {
  sub: string;
  data: { backendID: string; customerID: string };
}

/** Decode the stored access token for a customer (the `sub` + `data` claims). */
export function decodeToken(shortID: string): DecodedToken | null {
  const token = $customerAuth.get()[shortID]?.accessToken;
  if (!token) return null;
  try {
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
}

/** fetch with the customer's bearer token + standard headers. */
export async function authedFetch(shortID: string, url: string, init: RequestInit = {}): Promise<Response> {
  const { accessToken } = (await getAuth(shortID)) ?? {};
  return fetchWithVersion(url, {
    ...init,
    headers: {
      accept: 'application/json',
      ...(init.headers ?? {}),
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

export async function refresh(shortID: string) {
  const auth = $customerAuth.get()[shortID];
  if (!auth) {
    console.warn('cannot refresh: no auth');
    return;
  }
  const { refreshToken, accessToken } = auth;
  if (!refreshToken) {
    try {
      await checkAuth(shortID, accessToken);
      return;
    } catch {
      return logout(shortID);
    }
  }

  const base = await getInternalAppsiteUrl(shortID);
  const url = `${base}/api/refresh`;
  const res = await fetchWithVersion(url, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'X-Requested-With': 'checkinapp',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      refreshToken,
    }),
  });

  if (!res.ok) {
    console.log('could not refresh');
    logout(shortID);
    throw new Error('refresh_error');
  }
  if (res.status === 204) {
    // no content => noop
    return;
  }
  console.log('token refreshed');
  const json = await res.json();
  $customerAuth.set({ ...$customerAuth.get(),[shortID]: json });
}

async function checkAuth(shortID: string, token: string | undefined | null) {
  if (!token) {
    throw new Error('no_token');
  }

  const res = await fetch(
    `https://datamanager${import.meta.env.VITE_ENV === 'stage' ? '.cachena' : ''}.entrecode.de/api/${shortID}`,
    {
      headers: {
        authorization: `Bearer ${token}`,
      },
    },
  );

  if (!res.ok) {
    throw new Error('invalid_short_id');
  }

  const { account } = await res.json();

  if (!account) {
    throw new Error('outdated_token');
  }

  return true;
}

export function logout(customer: string) {
  const { [customer]: _removed, ...rest } = $customerAuth.get();
  $customerAuth.set(rest);
  // chat-state cleanup happens reactively in chat.ts (subscribes to $customerAuth).
}
